/*
 * 模块: 文件预处理器
 * 职责: 处理上传文件的预处理和验证
 * 依赖: 工具函数
 * 导出: 文件预处理函数
 * 限制: 文件<280行, 函数<30行
 */

import type { Period } from '../../../../types';
import type { FileInfo, UploadResult } from '../../types';
import { extractPeriodFromFilename, parseWeekNumber } from '../../utils/dateUtils';

/**
 * 文件预处理结果
 */
export interface PreprocessResult {
  fileInfos: FileInfo[];
  results: UploadResult[];
}

/**
 * 进度更新回调
 */
export type ProgressUpdater = (fileName: string, status: string) => void;

/**
 * 预处理文件：提取周期信息
 */
export const preprocessFiles = (
  fileList: File[],
  updateProgress: ProgressUpdater
): PreprocessResult => {
  const fileInfos: FileInfo[] = [];
  const results: UploadResult[] = [];

  for (const file of fileList) {
    const fileName = file.name;
    updateProgress(fileName, '检测周期...');

    // 从文件名提取周期
    const weekNumber = extractPeriodFromFilename(fileName);

    if (!weekNumber) {
      results.push({
        file: fileName,
        success: false,
        message: '无法从文件名识别周期格式，请使用 W_YYYY_MM_DD 或 Week_YYYY_MM_DD 格式'
      });
      updateProgress(fileName, '❌ 格式错误');
      continue;
    }

    // 解析周期编号
    const parsed = parseWeekNumber(weekNumber);
    if (!parsed) {
      results.push({
        file: fileName,
        success: false,
        message: '周期格式解析失败'
      });
      updateProgress(fileName, '❌ 解析失败');
      continue;
    }

    fileInfos.push({
      file,
      weekNumber,
      year: parsed.year,
      weekNum: parsed.weekNum
    });
  }

  return { fileInfos, results };
};

/**
 * 删除旧周期
 */
export const deleteOldPeriods = async (
  fileInfos: FileInfo[],
  periods: Period[],
  deleteFn: (periodId: number) => Promise<any>
): Promise<void> => {
  const periodsToDelete = fileInfos
    .map(info => periods.find(p => p.week_number === info.weekNumber))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);

  if (periodsToDelete.length > 0) {
    try {
      await Promise.all(periodsToDelete.map(p => deleteFn(p.id)));
    } catch (error) {
      console.warn('删除旧周期时出错:', error);
    }
  }
};

